import { AppError } from '@/shared/errors/appError'

type JsonRecord = Record<string, unknown>

type ApiRequestOptions = Omit<RequestInit, 'body'> & {
  body?: JsonRecord | FormData
  timeoutMs?: number
}

function isJsonRecord(value: unknown): value is JsonRecord {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function extractResponseMessage(payload: unknown): string | null {
  if (!isJsonRecord(payload)) return null
  if (typeof payload.message === 'string' && payload.message.trim().length > 0) {
    return payload.message
  }
  if (typeof payload.error === 'string' && payload.error.trim().length > 0) {
    return payload.error
  }
  return null
}

function buildBody(body: ApiRequestOptions['body']): BodyInit | undefined {
  if (!body) return undefined
  if (body instanceof FormData) return body
  return JSON.stringify(body)
}

export async function requestJson<T>(url: string, options: ApiRequestOptions = {}): Promise<T> {
  const controller = new AbortController()
  const timeoutMs = options.timeoutMs ?? 15000
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs)

  try {
    const isFormData = options.body instanceof FormData
    const headers = new Headers(options.headers || {})
    if (!isFormData && options.body && !headers.has('Content-Type')) {
      headers.set('Content-Type', 'application/json')
    }

    const response = await fetch(url, {
      ...options,
      headers,
      body: buildBody(options.body),
      signal: controller.signal,
    })

    const contentType = response.headers.get('content-type') || ''
    const hasJsonBody = contentType.includes('application/json')
    const payload = hasJsonBody ? ((await response.json()) as unknown) : null

    if (!response.ok) {
      const message = extractResponseMessage(payload) || `HTTP ${response.status}`
      throw new AppError(message, {
        code: 'HTTP_ERROR',
        status: response.status,
      })
    }

    return payload as T
  } catch (error) {
    if (error instanceof AppError) {
      throw error
    }

    if (error instanceof DOMException && error.name === 'AbortError') {
      throw new AppError('Превышено время ожидания ответа сервера.', {
        code: 'REQUEST_TIMEOUT',
        cause: error,
      })
    }

    throw new AppError('Ошибка сети при выполнении запроса.', {
      code: 'REQUEST_FAILED',
      cause: error,
    })
  } finally {
    clearTimeout(timeoutId)
  }
}
